import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ArrowLeft, Save, Upload } from 'lucide-react'
import { getAbout, updateAbout } from '../api/about'
import { uploadImage } from '../api/upload'
import { useAuth } from '../context/AuthContext'
import styles from './AboutEdit.module.css'

const EMPTY = {
  title: '',
  tagline: '',
  story: '',
  name: '',
  image: '',
  email: '',
  phone: '',
}

export default function AboutEdit() {
  const { token, user, loading: authLoading } = useAuth()
  const navigate = useNavigate()
  const fileInputRef = useRef(null)

  const [form, setForm] = useState(EMPTY)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!authLoading && (!user || user.role !== 'admin')) {
      navigate('/login', { replace: true })
    }
  }, [user, authLoading, navigate])

  useEffect(() => {
    getAbout()
      .then((data) =>
        setForm({
          title: data.title || '',
          tagline: data.tagline || '',
          story: data.story || '',
          name: data.name || '',
          image: data.image || '',
          email: data.email || '',
          phone: data.phone || '',
        })
      )
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }))

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploading(true)
    setError(null)
    try {
      const url = await uploadImage(file, token)
      setForm((f) => ({ ...f, image: url }))
    } catch {
      setError('Failed to upload image. Please try again.')
    } finally {
      setUploading(false)
      e.target.value = ''
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    setError(null)
    setSaved(false)
    try {
      await updateAbout(token, form)
      setSaved(true)
      setTimeout(() => setSaved(false), 3000)
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save changes.')
    } finally {
      setSaving(false)
    }
  }

  if (authLoading || !user) return null
  if (loading) return <div style={{ padding: '48px 24px', color: 'var(--text-muted)' }}>Loading…</div>

  return (
    <main className={styles.page}>
      <Link to="/dashboard" className={styles.back}>
        <ArrowLeft size={16} />
        Back to Dashboard
      </Link>

      <div className={styles.header}>
        <div>
          <h1 className={styles.heading}>About Us</h1>
          <p className={styles.subtitle}>Edit the public About Us page for Build With Mak</p>
        </div>
      </div>

      <form className={styles.form} onSubmit={handleSubmit}>
        <div className={styles.card}>
          <h2 className={styles.cardTitle}>Page Content</h2>
          <div className={styles.field}>
            <label className={styles.label}>Page Title</label>
            <input
              className={styles.input}
              value={form.title}
              onChange={set('title')}
              placeholder="About Build With Mak"
            />
          </div>
          <div className={styles.field}>
            <label className={styles.label}>Tagline</label>
            <input
              className={styles.input}
              value={form.tagline}
              onChange={set('tagline')}
              placeholder="We build your digital presence"
            />
          </div>
          <div className={styles.field}>
            <label className={styles.label}>Story / Bio</label>
            <textarea
              className={styles.textarea}
              rows={8}
              value={form.story}
              onChange={set('story')}
              placeholder="Tell visitors about Build With Mak…"
            />
          </div>
        </div>

        <div className={styles.card}>
          <h2 className={styles.cardTitle}>Photographer / Owner</h2>
          <div className={styles.twoCol}>
            <div className={styles.field}>
              <label className={styles.label}>Name</label>
              <input
                className={styles.input}
                value={form.name}
                onChange={set('name')}
                placeholder="Mak"
              />
            </div>
            <div className={styles.field}>
              <label className={styles.label}>Photo</label>
              <div className={styles.photoUpload}>
                {form.image && (
                  <img
                    src={form.image}
                    alt="Owner"
                    className={styles.photoPreview}
                  />
                )}
                <button
                  type="button"
                  className={styles.uploadBtn}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                >
                  <Upload size={14} />
                  {uploading ? 'Uploading…' : form.image ? 'Replace Photo' : 'Upload Photo'}
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className={styles.hiddenInput}
                  onChange={handleImageUpload}
                />
              </div>
            </div>
          </div>
        </div>

        <div className={styles.card}>
          <h2 className={styles.cardTitle}>Contact Info</h2>
          <div className={styles.twoCol}>
            <div className={styles.field}>
              <label className={styles.label}>Email</label>
              <input
                className={styles.input}
                type="email"
                value={form.email}
                onChange={set('email')}
                placeholder="hello@buildwithmak.com"
              />
            </div>
            <div className={styles.field}>
              <label className={styles.label}>Phone</label>
              <input
                className={styles.input}
                type="tel"
                value={form.phone}
                onChange={set('phone')}
                placeholder="+1 (555) 000-0000"
              />
            </div>
          </div>
        </div>

        {error && <p className={styles.error}>{error}</p>}

        <div className={styles.actions}>
          {saved && <span className={styles.savedMsg}>Changes saved</span>}
          <button type="submit" className={styles.saveBtn} disabled={saving || uploading}>
            <Save size={15} />
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </form>
    </main>
  )
}
